// analytics.js — thin PostHog wrapper with finance-safe defaults.
// Centralizes init, event tracking, user identify, and guards that prevent
// sending sensitive money values (debt balances, payment amounts) anywhere.
import posthog from "posthog-js";

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY || "";
// 2) Region host: US default. Use "https://eu.i.posthog.com" if you chose EU.
const POSTHOG_HOST = "https://us.i.posthog.com";

let _ready = false;

export function initAnalytics() {
  if (_ready || !POSTHOG_KEY) return;
  try {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      capture_pageview: false,     // single-page app — we track screen_viewed ourselves
      autocapture: true,           // still capture raw clicks for exploratory analysis
      session_recording: {
        maskAllInputs: true,       // NEVER record what users type (debt balances, etc.)
        maskTextSelector: "*",     // ALSO mask all displayed text — the dashboard shows
                                   // dollar amounts and replay records the screen.
      },
      persistence: "localStorage", // Capacitor webview — avoid cookie quirks
      // CapacitorHttp (enabled for CORS-free Ellie/Supabase calls) can't serialize
      // a gzip-compressed body and rejects it with 400. Send plain JSON instead.
      disable_compression: true,
    });
    // Capacitor webviews run on localhost, which PostHog ignores by default.
    posthog.opt_in_capturing();
    _ready = true;
  } catch (e) {
    // Analytics must never break the app.
    console.warn("[analytics] init failed", e);
  }
}

// Identify the signed-in user by their Supabase user id only — no email or PII.
export function identify(userId) {
  if (!_ready || !userId) return;
  try { posthog.identify(userId); } catch {}
}

export function resetAnalytics() {
  if (!_ready) return;
  try { posthog.reset(); } catch {}
}

// Safety net: keys we refuse to send even if a caller passes one by mistake.
const BLOCKED = [
  "balance", "amount", "rate", "minpayment", "min_payment", "debt",
  "total", "principal", "interest", "value", "price", "payment",
];

function sanitize(props = {}) {
  const clean = {};
  for (const [k, v] of Object.entries(props)) {
    if (BLOCKED.includes(String(k).toLowerCase())) continue; // drop financial values
    clean[k] = v;
  }
  return clean;
}

export function track(event, props = {}) {
  if (!_ready) return;
  try { posthog.capture(event, sanitize(props)); } catch {}
}
